import React, { useRef, useState, useEffect } from 'react';
import { Container, Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { ToastBox, notifyError, notifySuccess } from 'MyComponents/Toaster';
import { setEmailVerify, setwizardPageNumber, setKycToken, setEmail, setUserID } from '../../authslice';
import { RootState } from '../../store';
import config from '../../../config';
import email_icon from '../../../images/email_icon.png';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { getUserID } from '../../../Utils/user';
import { resendOtp, verifyOtpAndCreateProfile } from '../../../Utils/otp';
import OTPInput from '../../UI/OtpInput';
import ResendCode from '../../UI/ResendCode';

interface FormData {
  isEmailVerified: boolean;
}

interface EmailVerificationProps { }

const EmailVerification: React.FC<EmailVerificationProps & InjectedFormProps<FormData, EmailVerificationProps>> = ({ handleSubmit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const email = useSelector((state: RootState) => state.auth.email);
  const page = useSelector((state: RootState) => state.auth.wizardPageNumber);
  const invitationId = useSelector((state: RootState) => state.auth.invitationId);
  const entityId = useSelector((state: RootState) => state.auth.entityId);
  const firstName = useSelector((state: RootState) => state.auth.firstName);
  const lastName = useSelector((state: RootState) => state.auth.lastName);
  const dob = useSelector((state: RootState) => state.auth.registrationDob);
  const password = useSelector((state: RootState) => state.auth.password);

  const [validated, setValidated] = useState(false);
  const [validatedEmpty, setValidatedEmpty] = useState(false);
  const [validatedSuccess, setValidatedSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [maxResendsReached, setMaxResendsReached] = useState(false);
  const [verifyErrorCode, setVerifyErrorCode] = useState<string | null>(null);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  const registermini_baseURL = `${config.NODE_API_BASE_URL}/wso2/registermini`;

  const num1 = useRef<HTMLInputElement>(null);
  const num2 = useRef<HTMLInputElement>(null);
  const num3 = useRef<HTMLInputElement>(null);
  const num4 = useRef<HTMLInputElement>(null);
  const num5 = useRef<HTMLInputElement>(null);
  const num6 = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0 && !resendEnabled) {
      interval = setInterval(() => setTimer((prevTimer) => prevTimer - 1), 1000);
    } else if (timer === 0 && !resendEnabled) {
      setResendEnabled(true);
    }
    return () => clearInterval(interval);
  }, [timer, resendEnabled]);

  const verifyOtp = async (userEnteredOtp: string, userId: string) => {
    try {
      const requestData: any = { userId, userEnteredOtp };
      if (invitationId) requestData.invitationId = invitationId;
      if (entityId) requestData.linked_entity_id = entityId;

      const data = await verifyOtpAndCreateProfile(requestData);
      if (data?.status === 1) {
        setValidated(false);
        setVerifyErrorCode(null);
      } else {
        setValidated(true);
        setVerifyErrorCode(data?.code ?? null);
        fieldsEmpty();
      }
      return data;
    } catch (error: any) {
      console.error(error);
      setValidated(true);
      fieldsEmpty();
    }
  };

  const resendCode = async () => {
    try {
      setValidated(false);
      setValidatedEmpty(false);
      setValidatedSuccess(false);
      setResendEnabled(false);
      const data = await resendOtp(email);
      if (data?.status === 1) {
        setValidatedSuccess(true);
        notifySuccess('OTP Sent');
        const nextResendSeconds = data.nextResendAt
          ? Math.max(Math.ceil((new Date(data.nextResendAt).getTime() - Date.now()) / 1000), 1)
          : 30;
        setTimer(nextResendSeconds);
      } else if (data?.code === 'MAX_RESENDS_REACHED') {
        setMaxResendsReached(true);
        notifyError(data.msg || 'Maximum resend attempts reached.');
      } else if (data?.code === 'TOO_SOON' || data?.code === 'LOCKED') {
        setTimer(data.retryAfterSeconds || 30);
        notifyError(data.msg || 'Please wait before requesting another code.');
      } else {
        notifyError('Something Went Wrong');
        setResendEnabled(true);
        fieldsEmpty();
      }
    } catch (error: any) {
      notifyError('Something Went Wrong');
      setResendEnabled(true);
      fieldsEmpty();
    }
  };

  const submitForm = async () => {
    setIsSubmitted(true);
    const userEnteredOtp = `${num1.current?.value}${num2.current?.value}${num3.current?.value}${num4.current?.value}${num5.current?.value}${num6.current?.value}`;
    if (!userEnteredOtp) {
      setValidatedEmpty(true);
      return;
    }
    setLoading(true);
    const userId = await getUserID(email);
    const response = await verifyOtp(userEnteredOtp, userId);
    if (response?.status === 1) {
      if (response.profile && response.profile.kyc_token) {
        dispatch(setKycToken(response.profile.kyc_token));
      }
      dispatch(setEmailVerify(true));
      dispatch(setwizardPageNumber(page + 1));
    }
    setIsSubmitted(false);
  };

  const handleOtpInput = (e: React.ChangeEvent<HTMLInputElement>, nextInputRef: React.RefObject<HTMLInputElement> | null) => {
    const value = e.target.value;
    setValidatedEmpty(false);
    setValidatedSuccess(false);
    setValidated(false);
    if (value.length === 1 && /^\d$/.test(value)) {
      if (nextInputRef && nextInputRef.current) nextInputRef.current.focus();
    } else {
      e.target.value = '';
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (/^\d+$/.test(pastedText) && pastedText.length === 6) {
      const otpArray = pastedText.split('');
      [num1, num2, num3, num4, num5, num6].forEach((ref, index) => {
        if (ref.current) ref.current.value = otpArray[index];
      });
      num6.current?.focus();
      e.preventDefault();
    }
  };

  const handleBackspace = (currentRef: React.RefObject<HTMLInputElement>, prevRef: React.RefObject<HTMLInputElement> | null) => {
    if (currentRef.current?.value === '' && prevRef?.current) {
      prevRef.current.focus();
      prevRef.current.value = '';
    } else {
      currentRef.current!.value = '';
    }
  };

  const fieldsEmpty = () => {
    [num1, num2, num3, num4, num5, num6].forEach((ref) => {
      if (ref.current) ref.current.value = '';
    });
  };

  const handleEmailUpdate = async () => {
    const trimmed = newEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailChangeError('Please enter a valid email address.');
      return;
    }
    setEmailChangeLoading(true);
    setEmailChangeError('');
    try {
      const response = await axios.post(registermini_baseURL, {
        email: trimmed,
        firstName,
        lastName,
        dob,
        password,
        account_type: 'personal',
      });
      dispatch(setEmail(trimmed));
      dispatch(setUserID(response.data.userId));
      setNewEmail('');
      setShowChangeEmail(false);
      fieldsEmpty();
      setValidated(false);
      setVerifyErrorCode(null);
      setMaxResendsReached(false);
      setTimer(30);
      setResendEnabled(false);
    } catch (error: any) {
      setEmailChangeError(
        error?.response?.data?.message || 'Failed to update email. Please try again.'
      );
    } finally {
      setEmailChangeLoading(false);
    }
  };

  return (
    <>
      <ToastBox />
      <Container fluid className="flex-grow-1 d-flex flex-column align-items-center justify-content-center py-5">
        <div className="text-center" style={{ maxWidth: 480, width: "100%" }}>
          <img src={email_icon} width={50} alt="email icon" />
          <h4 className="mt-3 fw-bold">{t('register.verify_email_title')}</h4>
          <span className="fw-normal medium">
            {t('register.code_sent')} <br />
            <span className="fw-bold medium">{email}</span>. {t('register.code_valid')}&nbsp;
            <span
              className="text-primary medium"
              style={{ cursor: 'pointer' }}
              onClick={() => { setShowChangeEmail((p) => !p); setEmailChangeError(''); }}
            >
              {t('register.change_email')}
            </span>

            {showChangeEmail && (
              <div className="mt-3 d-flex justify-content-center align-items-center flex-wrap gap-2">
                <input
                  type="email"
                  className="form-control rounded-5 py-1"
                  style={{ maxWidth: 260 }}
                  placeholder="Enter new email address"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setEmailChangeError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailUpdate()}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-5 px-3"
                  onClick={handleEmailUpdate}
                  disabled={emailChangeLoading}
                >
                  {emailChangeLoading
                    ? <Spinner as="span" size="sm" animation="border" />
                    : 'Send OTP'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="rounded-5 px-3"
                  onClick={() => { setShowChangeEmail(false); setNewEmail(''); setEmailChangeError(''); }}
                >
                  Cancel
                </Button>
                {emailChangeError && (
                  <p className="text-danger small w-100 text-center mb-0">{emailChangeError}</p>
                )}
              </div>
            )}
          </span>
          <p className="fs-6 mt-3 letter-spacing-fix fw-medium">{t('kyc.confirm_secure')}</p>
          <Form noValidate onSubmit={handleSubmit(submitForm)}>
            <div className="input-group d-flex justify-content-center">
              <div className="input-group d-flex justify-content-center">
                <OTPInput inputRef={num1} nextRef={num2} prevRef={null} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                <OTPInput inputRef={num2} nextRef={num3} prevRef={num1} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                <OTPInput inputRef={num3} nextRef={num4} prevRef={num2} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                <OTPInput inputRef={num4} nextRef={num5} prevRef={num3} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                <OTPInput inputRef={num5} nextRef={num6} prevRef={num4} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                <OTPInput inputRef={num6} nextRef={null} prevRef={num5} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
              </div>
              {validated && (
                <p className="text-danger fw-bold small text-start mt-2 mb-0">
                  {verifyErrorCode === 'OTP_EXPIRED'
                    ? t('common.otp_expired', 'This code has expired. Please resend.')
                    : verifyErrorCode === 'LOCKED'
                    ? t('common.otp_locked', 'Too many attempts. Please try again later.')
                    : t('common.otp_invalid')}
                </p>
              )}
              {validatedEmpty && <p className="text-danger fw-bold small text-start mt-2 mb-0">{t('common.otp_enter_valid')}</p>}
              {validatedSuccess && <p className="text-success fw-bold small text-start mt-2 mb-0">{t('common.otp_sent')}</p>}
            </div>
            <div className="d-grid gap-2 col-md-8 mx-auto mt-3">
              <Button variant="primary" type="submit" className="btn btn-primary rounded-5 mt-3" disabled={loading && isSubmitted}>
                {loading && isSubmitted && (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mx-2" />
                )}
                <span>{t('common.submit')}</span>
              </Button>
            </div>
          </Form>
          <ResendCode timer={timer} resendEnabled={resendEnabled} resendCode={resendCode} maxResendsReached={maxResendsReached} />
        </div>
      </Container>
    </>
  );
};

const form = reduxForm<FormData, EmailVerificationProps>({
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: 'wizard',
  enableReinitialize: true,
})(EmailVerification);

export default connect(null)(form);
